import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Avatar,
  Divider,
  Paper,
  useTheme,
  useMediaQuery,
  InputBase,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Badge,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup
} from '@mui/material';
import {
  Store as StoreIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckCircleIcon,
  Clear as ClearIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  FilterList as FilterListIcon,
  Add as AddIcon,
  Male,
  Female
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import BottomNavigation from '../components/BottomNavigation';
import { homeAPI } from '../services/api';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContextNew';
import SignUpPromptModal from '../components/SignUpPromptModal';
import { formatCurrency, getCountryCode } from '../utils/countryUtils';

// Styled components - same as Offers screen
const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '20ch',
    },
  },
}));

const FeaturedServices = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { addToCart, isInCart, getItemQuantity, updateItemQuantity, removeFromCart, items } = useCart();
  const { isGuest } = useAuth();

  // Gender icon helper function
  const getGenderIcon = (gender) => {
    if (!gender || gender.trim() === '') {
      return null;
    }
    
    switch (gender) {
      case 'Male':
        return <Male sx={{ fontSize: 16, color: '#1976d2' }} />;
      case 'Female':
        return <Female sx={{ fontSize: 16, color: '#e91e63' }} />;
      case 'Both':
      case 'Unisex':
        return (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Male sx={{ fontSize: 14, color: '#1976d2' }} />
            <Female sx={{ fontSize: 14, color: '#e91e63' }} />
          </Box>
        );
      default:
        return null;
    }
  };

  // State variables
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProviderTypes, setSelectedProviderTypes] = useState(['salon', 'pros']); // Default both selected
  const [filteredResults, setFilteredResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [distanceType, setDistanceType] = useState('distance');
  const [distanceValue, setDistanceValue] = useState(50);
  const [minRating, setMinRating] = useState(0);
  const [userLocation, setUserLocation] = useState(null);
  const [sortBy, setSortBy] = useState('rating-high');

  // State for featured services data
  const [featuredServices, setFeaturedServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesError, setServicesError] = useState(null);
  
  // Guest mode states
  const [showSignUpPrompt, setShowSignUpPrompt] = useState(false);
  const [restrictedFeature, setRestrictedFeature] = useState('');

  // Guest mode handlers
  const handleGuestRestriction = (featureName) => {
    setRestrictedFeature(featureName);
    setShowSignUpPrompt(true);
  };

  // Cart handling functions
  const handleServiceAddToCart = (service) => {
    if (isGuest) {
      handleGuestRestriction('book services');
      return;
    }
    
    // Ensure service has a proper ID and all required fields
    const serviceWithId = {
      ...service,
      id: service.id || service._id,
      name: service.name || service.serviceName,
      price: service.price || service.basePrice,
      image: service.image || service.displayImage || '/placeholder-service.jpg',
      duration: service.duration,
      providerName: service.providerName || service.provider?.businessName || 'Provider',
      itemType: 'service',
      providerId: service.providerId || service.provider?._id || 'Unknown Provider'
    };
    
    addToCart(serviceWithId);
  };

  // Helper functions for cart operations
  const isItemInCart = (service) => {
    return isInCart(service.id || service._id);
  };

  const getItemQuantityInCart = (service) => {
    return getItemQuantity(service.id || service._id);
  };

  // Fetch featured services data from backend using the same source as home page
  const fetchFeaturedServices = async () => {
    try {
      setServicesLoading(true);
      setServicesError(null);
      
      // Get user location and address for distance calculation
      const savedLocation = localStorage.getItem('userLocation');
      const savedAddress = localStorage.getItem('userAddress');
      let locationParams = {};
      if (savedLocation) {
        try {
          const locationData = JSON.parse(savedLocation);
          if (locationData.latitude && locationData.longitude) {
            locationParams = {
              latitude: locationData.latitude,
              longitude: locationData.longitude,
              address: savedAddress // Include address for city filtering
            };
          }
        } catch (error) {
          console.error('Error parsing saved location:', error);
        }
      }
      
      const response = await homeAPI.getAllData(locationParams);
      
      if (response.data && response.data.services) {
        const services = response.data.services.map(service => ({
          ...service,
          // Ensure consistent field names for UI rendering
          serviceName: service.serviceName || service.name || 'Service',
          providerName: service.providerName || service.provider?.businessName || 'Provider',
          providerType: service.providerType || service.provider?.providerType || 'salon',
          rating: service.rating || service.averageRating || 5,
          reviews: service.reviews || service.reviewCount || service.totalReviews || 0,
          price: service.price || service.basePrice || 0,
          distance: service.distance || 'Distance unavailable', // Backend calculates real distance
          duration: service.duration || 30,
          discount: service.originalPrice && service.price ? 
            `${Math.round(((service.originalPrice - service.price) / service.originalPrice) * 100)}% OFF` : 
            'Featured Service'
        }));
        setFeaturedServices(services);
      } else {
        setFeaturedServices([]);
      }
    } catch (error) {
      console.error('Error fetching featured services:', error);
      setServicesError('Failed to fetch featured services. Please try again later.');
      setFeaturedServices([]);
    } finally {
      setServicesLoading(false);
    }
  };

  // Fetch featured services when component mounts or location changes
  useEffect(() => {
    fetchFeaturedServices();
  }, []);
  
  // Refetch when user location changes
  useEffect(() => {
    const handleLocationChange = () => {
      fetchFeaturedServices();
    };
    
    window.addEventListener('locationUpdated', handleLocationChange);
    return () => window.removeEventListener('locationUpdated', handleLocationChange);
  }, []);

  // Initialize filtered results when featured services are loaded
  useEffect(() => {
    if (featuredServices.length > 0) {
      setFilteredResults(featuredServices);
    }
  }, [featuredServices]);

  // Get services data based on current state
  const getServicesData = () => {
    return featuredServices;
  };

  // Handle provider type selection
  const handleProviderTypeToggle = (providerType) => {
    setSelectedProviderTypes(prev => 
      prev.includes(providerType)
        ? prev.filter(type => type !== providerType)
        : [...prev, providerType]
    );
  };

  // Get user location from localStorage (set by home screen)
  useEffect(() => {
    const savedLocation = localStorage.getItem('userLocation');
    if (savedLocation) {
      try {
        setUserLocation(JSON.parse(savedLocation));
      } catch (error) {
        console.error('Error parsing user location:', error);
      }
    }
  }, []);

  // Filter results based on search, provider types, and rating
  useEffect(() => {
    if (featuredServices.length === 0) return; // Don't filter if no data yet
    
    setLoading(true);
    setPage(1);
    
    const rawData = getServicesData();
    
    let filtered = rawData.filter(result => {
      // Advanced search filter - similar to CategoryScreen
      let matchesSearch = true;
      if (searchQuery.trim()) {
        const searchWords = searchQuery.toLowerCase().trim().split(/\s+/).filter(word => word.length > 0);
        
        // Search across multiple fields: service name, provider name, provider type, service description, etc.
        const serviceName = (result.serviceName || result.name || '').toLowerCase();
        const providerName = (result.providerName || result.provider?.businessName || '').toLowerCase();
        const providerType = (result.providerType || result.provider?.providerType || '').toLowerCase();
        const serviceDescription = (result.description || '').toLowerCase();
        const serviceCategory = (result.category || '').toLowerCase();
        
        // Check if ANY search word is found in any of the searchable fields (OR logic)
        matchesSearch = searchWords.some(searchWord => {
          return serviceName.includes(searchWord) ||
                 providerName.includes(searchWord) ||
                 providerType.includes(searchWord) ||
                 serviceDescription.includes(searchWord) ||
                 serviceCategory.includes(searchWord);
        });
      }
      
      // Handle provider type filtering - check both direct and nested providerType
      const resultProviderType = result.providerType || result.provider?.providerType;
      const matchesProviderType = selectedProviderTypes.includes(resultProviderType);
      
      // Filter by rating (whole number ranges)
      let matchesRating = true;
      if (minRating > 0) {
        const minRatingValue = minRating;
        const maxRatingValue = minRating + 0.9;
        const rating = result.rating || result.averageRating || 0;
        matchesRating = rating >= minRatingValue && rating <= maxRatingValue;
      }
      
      // Distance filtering removed as per user request
      
      return matchesSearch && matchesProviderType && matchesRating;
    });
    
    // Sort results
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'rating-high':
          return (b.rating || 0) - (a.rating || 0);
        case 'rating-low':
          return (a.rating || 0) - (b.rating || 0);
        case 'distance':
          const distanceA = parseFloat((a.distance || '0 km').replace(' km', ''));
          const distanceB = parseFloat((b.distance || '0 km').replace(' km', ''));
          return distanceA - distanceB;
        case 'name':
          return (a.serviceName || a.name || '').localeCompare(b.serviceName || b.name || '');
        case 'price-low':
          return parseFloat((a.price || '0').toString().replace('$', '')) - parseFloat((b.price || '0').toString().replace('$', ''));
        case 'price-high':
          return parseFloat((b.price || '0').toString().replace('$', '')) - parseFloat((a.price || '0').toString().replace('$', ''));
        default:
          return (b.rating || 0) - (a.rating || 0);
      }
    });
    
    setFilteredResults(filtered);
    setLoading(false);
  }, [featuredServices, searchQuery, selectedProviderTypes, distanceType, distanceValue, minRating, sortBy, userLocation]);

  // Infinite scroll functionality
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 1000) {
        if (hasMore && !loading) {
          loadMore();
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMore, loading]);

  // Load more results (infinite scroll simulation)
  const loadMore = () => {
    if (hasMore && !loading) {
      setLoading(true);
      setTimeout(() => {
        setPage(prev => prev + 1);
        setHasMore(page < 3);
        setLoading(false);
      }, 1000);
    }
  };

  // Auto-scroll images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex(prev => {
        const newIndex = {};
        getServicesData().forEach(result => {
          newIndex[result.id] = ((prev[result.id] || 0) + 1) % 1; // Single image for now
        });
        return newIndex;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  // Dynamic placeholder
  const placeholders = [
    "Search for services...",
    "Search for treatments...", 
    "Search for beauty services...",
    "Search for salon services...",
    "Search for provider names...",
    "Search for service categories..."
  ];
  
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex(prev => (prev + 1) % placeholders.length);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  // Clear all filters
  const clearFilters = () => {
    setSelectedProviderTypes(['salon', 'pros']);
    setSearchQuery('');
    setDistanceType('distance');
    setDistanceValue(50);
    setMinRating(0);
    setSortBy('rating-high');
  };

  // Display current location info
  const getLocationInfo = () => {
    if (userLocation) {
      if (userLocation.fullAddress) {
        return `📍 ${userLocation.fullAddress}`;
      } else if (userLocation.address) {
        return `📍 ${userLocation.address}`;
      } else if (userLocation.city) {
        return `📍 ${userLocation.city}`;
      } else {
        return '📍 Current Location';
      }
    }
    return '📍 Location not set';
  };

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ backgroundColor: '#1976d2' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Featured Services
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ 
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: '100%',
          backgroundColor: '#f5f5f5',
          py: 2 
        }}>
        {/* Search Bar with Filter */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Search sx={{ flex: 1 }}>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
                             <StyledInputBase
                 placeholder={placeholders[placeholderIndex]}
                 value={searchQuery}
                 onChange={(e) => {
                   const value = e.target.value;
                  setSearchQuery(value);
                 }}
                 sx={{ width: '100%' }}
               />
               {searchQuery && (
                 <IconButton
                   size="small"
                   onClick={() => setSearchQuery('')}
                   sx={{ 
                     position: 'absolute', 
                     right: 8, 
                     color: 'rgba(0,0,0,0.5)',
                     '&:hover': { color: 'rgba(0,0,0,0.8)' }
                   }}
                 >
                   <ClearIcon />
                 </IconButton>
               )}
            </Search>
            <IconButton
              onClick={() => setFilterDialogOpen(true)}
              sx={{ 
                backgroundColor: '#1976d2',
                color: 'white',
                borderRadius: '50%',
                '&:hover': { 
                  backgroundColor: '#1976d2dd',
                  transform: 'scale(1.05)',
                  boxShadow: '0 4px 12px #1976d240'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <FilterListIcon />
            </IconButton>
          </Box>
                     {/* Location Info */}
           <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
             <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
               {getLocationInfo()}
             </Typography>
             <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
               • Within {distanceValue}km radius
             </Typography>
           </Box>
           
           {/* Search Results Counter */}
           {searchQuery.trim() && (
             <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
               <Typography variant="caption" color="primary" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                 🔍 Search: "{searchQuery}" - {filteredResults.length} results found
               </Typography>
             </Box>
           )}
           
           {/* Search Help Text */}
           <Box sx={{ mt: 1 }}>
             <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
               💡 Search by service name, provider name, provider type, service description, or categories
             </Typography>
           </Box>
        </Paper>

        {/* Provider Type Filter */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: 2 }}>
            Provider Type
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant={selectedProviderTypes.includes('salon') ? 'contained' : 'outlined'}
              startIcon={<StoreIcon />}
              onClick={() => handleProviderTypeToggle('salon')}
              sx={{
                flex: 1,
                backgroundColor: selectedProviderTypes.includes('salon') ? '#1976d2' : 'transparent',
                color: selectedProviderTypes.includes('salon') ? 'white' : '#1976d2',
                borderColor: '#1976d2',
                borderRadius: '25px',
                py: 1.5,
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: selectedProviderTypes.includes('salon') ? '#1976d2dd' : '#1976d215',
                  transform: 'translateY(-1px)',
                  boxShadow: selectedProviderTypes.includes('salon') ? '0 4px 12px #1976d240' : '0 2px 8px #1976d230'
                },
                transition: 'all 0.2s ease'
              }}
            >
              Salon
            </Button>
            <Button
              variant={selectedProviderTypes.includes('pros') ? 'contained' : 'outlined'}
              startIcon={<PersonIcon />}
              onClick={() => handleProviderTypeToggle('pros')}
              sx={{
                flex: 1,
                backgroundColor: selectedProviderTypes.includes('pros') ? '#1976d2' : 'transparent',
                color: selectedProviderTypes.includes('pros') ? 'white' : '#1976d2',
                borderColor: '#1976d2',
                borderRadius: '25px',
                py: 1.5,
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: selectedProviderTypes.includes('pros') ? '#1976d2dd' : '#1976d215',
                  transform: 'translateY(-1px)',
                  boxShadow: selectedProviderTypes.includes('pros') ? '0 4px 12px #1976d240' : '0 2px 8px #1976d230'
                },
                transition: 'all 0.2s ease'
              }}
            >
              Pros
            </Button>
          </Box>
        </Box>

        {/* Results Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
              Featured Services
              {selectedProviderTypes.length > 0 && selectedProviderTypes.length < 2 && (
                <span style={{ color: '#666', fontWeight: 'normal' }}>
                  {' '}({selectedProviderTypes[0] === 'salon' ? 'Salon' : 'Pros'})
                </span>
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredResults.length} results
            </Typography>
          </Box>

          {servicesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : servicesError ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="error">
                {servicesError}
              </Typography>
              <Button 
                variant="outlined" 
                onClick={fetchFeaturedServices}
                sx={{ mt: 2 }}
              >
                Try Again
              </Button>
            </Paper>
          ) : loading && page === 1 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredResults.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No featured services found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Try adjusting your search or filters
              </Typography>
            </Paper>
          ) : (
            <>
              <Grid container spacing={{ xs: 1.5, sm: 2, md: 2 }}>
                {filteredResults.map((result) => (
                  <Grid item xs={12} sm={6} md={4} key={result.id || result._id}>
                    <Card 
                      onClick={() => navigate(`/service/${result._id || result.id}/${result.providerId || ''}`)}
                      sx={{ 
                        height: '100%', 
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                        },
                        borderRadius: { xs: 2, sm: 3 },
                        overflow: 'hidden',
                        border: 'none',
                        position: 'relative',
                        backgroundColor: 'white'
                      }}
                    >
                      {/* Image Section */}
                      <Box sx={{ position: 'relative', height: { xs: 180, sm: 200 } }}>
                        <CardMedia
                          component="img"
                          height="200"
                          image={result.image || result.displayImage || 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=300&h=200&fit=crop'}
                          alt={result.serviceName || result.name}
                          sx={{ 
                            objectFit: 'cover',
                            width: '100%',
                            height: { xs: 180, sm: 200 },
                            maxHeight: { xs: 180, sm: 200 }
                          }}
                          onError={(e) => {
                            e.target.src = '/placeholder-service.jpg';
                          }}
                        />
                        
                        {/* Featured Badge - Top Left */}
                        <Chip 
                          label="Featured Service" 
                          size="small" 
                          sx={{ 
                          position: 'absolute',
                            top: { xs: 6, sm: 8 }, 
                            left: { xs: 6, sm: 8 },
                            backgroundColor: '#ffd700',
                            color: '#333',
                          fontWeight: 'bold',
                            fontSize: { xs: '0.6rem', sm: '0.7rem' },
                            height: { xs: 18, sm: 20 }
                          }}
                        />
                        
                        {/* Favorite Button - Top Right */}
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            // TODO: Add favorite functionality
                          }}
                          size="small"
                          sx={{ 
                            position: 'absolute', 
                            top: { xs: 6, sm: 8 }, 
                            right: { xs: 6, sm: 8 },
                            backgroundColor: 'rgba(255,255,255,0.9)',
                            color: '#666',
                            '&:hover': { 
                              backgroundColor: 'rgba(255,255,255,1)',
                              color: '#1976d2'
                            }
                          }}
                        >
                          <FavoriteBorderIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                        </IconButton>
                      </Box>
                      
                      {/* Content Section */}
                      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                        {/* Service Name */}
                        <Box sx={{ mb: { xs: 0.5, sm: 0.5 } }}>
                          <Typography variant="subtitle1" sx={{ 
                          fontWeight: 'bold', 
                            fontSize: { xs: '0.85rem', sm: '0.9rem' },
                            lineHeight: 1.2,
                            color: '#1a1a1a',
                            cursor: 'pointer',
                            wordBreak: 'break-word',
                            '&:hover': {
                              color: '#1976d2',
                              textDecoration: 'underline'
                            }
                        }}>
                          {result.serviceName || result.name || 'Service'}
                        </Typography>
                        </Box>
                        
                        {/* Provider Name - Below Service Name */}
                          <Typography variant="body2" color="text.secondary" sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.8rem' },
                          fontWeight: 500,
                          mb: { xs: 0.75, sm: 1 }
                          }}>
                            by {result.providerName || 'Provider'}
                          </Typography>
                          
                        {/* Row 1: Rating, Distance, and Provider Type Chip */}
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: { xs: 0.5, sm: 0.5 },
                          flexWrap: 'wrap',
                          gap: { xs: 0.5, sm: 1 }
                        }}>
                          {/* Rating */}
                          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                            <StarIcon sx={{ color: '#ffc107', fontSize: { xs: 14, sm: 16 }, mr: 0.5, flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.85rem' }, mr: 0.5, flexShrink: 0 }}>
                              {result.rating > 0 ? result.rating.toFixed(1) : '5.0'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, fontWeight: 500, flexShrink: 0 }}>
                              ({result.reviews || 0})
                            </Typography>
                          </Box>
                          
                          {/* Distance */}
                          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                            <LocationIcon sx={{ color: '#1976d2', fontSize: { xs: 14, sm: 16 }, mr: 0.5, flexShrink: 0 }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' }, fontWeight: 500 }}>
                              {result.distance || 'N/A'}
                            </Typography>
                          </Box>
                          
                          {/* Provider Type Chip */}
                          <Chip
                            label={(result.providerType === 'salon' || result.provider?.providerType === 'salon') ? 'Salon' : 'Pros'}
                            size="small"
                            sx={{
                              fontSize: { xs: '0.6rem', sm: '0.65rem' },
                              height: { xs: 18, sm: 20 },
                              fontWeight: 'bold',
                              flexShrink: 0,
                              backgroundColor: (result.providerType === 'salon' || result.provider?.providerType === 'salon') ? '#1976d2' : '#dc004e',
                              color: 'white',
                              '& .MuiChip-label': { px: { xs: 0.75, sm: 1 } }
                            }}
                          />
                        </Box>
                        
                        {/* Row 2: Duration and Gender */}
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: { xs: 1, sm: 1.5 }
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <TimeIcon sx={{ color: '#666', fontSize: { xs: 14, sm: 16 }, mr: 0.5 }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                              {result.duration ? `${result.duration} mins` : '30 mins'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getGenderIcon(result.gender) || (
                              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                <Male sx={{ fontSize: { xs: 12, sm: 14 }, color: '#1976d2' }} />
                                <Female sx={{ fontSize: { xs: 12, sm: 14 }, color: '#e91e63' }} />
                              </Box>
                            )}
                          </Box>
                        </Box>
                        
                        {/* Price and Add to Cart Button */}
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between'
                        }}>
                          <Box>
                            <Typography variant="h6" sx={{ 
                              fontWeight: 'bold', 
                              color: '#1976d2', 
                              fontSize: { xs: '1.1rem', sm: '1.25rem' },
                              lineHeight: 1
                            }}>
                              {formatCurrency(result.price || result.basePrice || 0)}
                            </Typography>
                            {result.originalPrice && (
                              <Typography variant="caption" sx={{ 
                                textDecoration: 'line-through', 
                                color: '#999', 
                                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                display: 'block',
                                mt: 0.25
                              }}>
                                {formatCurrency(result.originalPrice)}
                              </Typography>
                            )}
                          </Box>
                          {isItemInCart(result) ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 } }}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const currentQuantity = getItemQuantityInCart(result);
                                  if (currentQuantity > 1) {
                                    updateItemQuantity(result, currentQuantity - 1);
                                  } else {
                                    const cartItem = items.find(item => 
                                      item.id === (result.id || result._id) && 
                                      item.itemType === 'service'
                                    );
                                    if (cartItem) {
                                      removeFromCart(cartItem.cartItemId);
                                    }
                                  }
                                }}
                                sx={{ 
                                  backgroundColor: '#f5f5f5',
                                  '&:hover': { backgroundColor: '#e0e0e0' },
                                  width: { xs: 26, sm: 28 },
                                  height: { xs: 26, sm: 28 }
                                }}
                              >
                                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, fontWeight: 'bold' }}>-</Typography>
                              </IconButton>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: { xs: 18, sm: 20 }, textAlign: 'center', fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                                {getItemQuantityInCart(result)}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const currentQuantity = getItemQuantityInCart(result);
                                  updateItemQuantity(result, currentQuantity + 1);
                                }}
                                sx={{ 
                                  backgroundColor: '#f5f5f5',
                                  '&:hover': { backgroundColor: '#e0e0e0' },
                                  width: { xs: 26, sm: 28 },
                                  height: { xs: 26, sm: 28 }
                                }}
                              >
                                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, fontWeight: 'bold' }}>+</Typography>
                              </IconButton>
                            </Box>
                          ) : (
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleServiceAddToCart(result);
                              }}
                              sx={{ 
                                backgroundColor: '#1976d2',
                                '&:hover': { backgroundColor: '#1565c0' },
                                fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                minHeight: { xs: '26px', sm: '28px' },
                                minWidth: { xs: '70px', sm: '80px' },
                                maxHeight: { xs: '26px', sm: '28px' },
                                padding: { xs: '4px 6px', sm: '4px 8px' },
                                whiteSpace: 'nowrap',
                                textTransform: 'none',
                                '& .MuiButton-startIcon': {
                                  margin: { xs: '0 2px 0 -2px', sm: '0 4px 0 -4px' }
                                }
                              }}
                            >
                              Add to Cart
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              
              {/* Loading indicator for infinite scroll */}
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Filter Dialog */}
      <Dialog 
        open={filterDialogOpen} 
        onClose={() => setFilterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Filters</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button onClick={clearFilters} size="small">
                Clear All
              </Button>
              <IconButton
                onClick={() => setFilterDialogOpen(false)}
                size="small"
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { color: 'text.primary' }
                }}
              >
                <ClearIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            {/* Distance Filter */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Distance
              </Typography>
              <Box sx={{ mt: 2, px: 2 }}>
                <Slider
                  value={distanceValue}
                  onChange={(e, newValue) => setDistanceValue(newValue)}
                  min={1}
                  max={100}
                  step={1}
                  marks={[
                    { value: 1, label: '1 km' },
                    { value: 25, label: '25 km' },
                    { value: 50, label: '50 km' },
                    { value: 75, label: '75 km' },
                    { value: 100, label: '100 km' }
                  ]}
                  valueLabelDisplay="auto"
                />
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
                  Within {distanceValue} km
                </Typography>
              </Box>
            </Box>

            {/* Rating Filter */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Minimum Rating
              </Typography>
              <Box sx={{ px: 2 }}>
                <Slider
                  value={minRating}
                  onChange={(e, newValue) => setMinRating(newValue)}
                  min={0}
                  max={5}
                  step={0.5}
                  marks={[
                    { value: 0, label: 'Any' },
                    { value: 2.5, label: '2.5+' },
                    { value: 5, label: '5.0' }
                  ]}
                  valueLabelDisplay="auto"
                />
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  {minRating === 0 ? 'Any rating' : `${minRating}+ stars`}
                </Typography>
              </Box>
            </Box>

            {/* Sort By */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Sort By
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  size="small"
                >
                  <MenuItem value="rating-high">Rating: High to Low</MenuItem>
                  <MenuItem value="rating-low">Rating: Low to High</MenuItem>
                  <MenuItem value="distance">Distance: Nearest First</MenuItem>
                  <MenuItem value="name">Name: A to Z</MenuItem>
                  <MenuItem value="price-low">Price: Low to High</MenuItem>
                  <MenuItem value="price-high">Price: High to Low</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterDialogOpen(false)}>
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sign Up Prompt Modal for Guest Users */}
      <SignUpPromptModal
        open={showSignUpPrompt}
        onClose={() => setShowSignUpPrompt(false)}
        featureName={restrictedFeature}
      />

      {/* Bottom Navigation */}
      <BottomNavigation />
    </Box>
  );
};

export default FeaturedServices;
